import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// 권한 레벨 정의 (숫자가 낮을수록 높은 권한)
const ROLE_LEVELS = {
    'ROLE_ADMIN': 1,
    'ROLE_MANAGER': 2,
    'ROLE_USER': 3,
};

export const useAuthStore = create(
    persist(
        (set, get) => ({
            user: null,
            // 토큰은 HttpOnly 쿠키로 관리되므로 store에서는 제거
            isAuthenticated: false,

            login: (userData) => {
                // 토큰은 HttpOnly 쿠키로 자동 관리됨
                // localStorage 사용 제거
                set({
                    user: userData,
                    isAuthenticated: userData.isActive === true,
                });
            },

            logout: () => {
                // 토큰은 서버 측 /logout API를 통해 쿠키에서 제거됨
                set({
                    user: null,
                    isAuthenticated: false,
                });
            },

            // 사용자 이름 가져오기
            getUserName: () => {
                const { user } = get();
                return user?.name || '';
            },

            // 사용자의 모든 roleType 가져오기
            getUserRoleTypes: () => {
                const { user } = get();
                if (!user?.userRoles) return [];
                return user.userRoles.map(ur => ur.roleType).filter(Boolean);
            },

            // 사용자의 최고 권한 레벨 가져오기 (숫자가 낮을수록 높은 권한)
            getHighestRoleLevel: () => {
                const { user } = get();
                if (!user?.userRoles || user.userRoles.length === 0) return 999;

                const levels = user.userRoles
                    .map(ur => ROLE_LEVELS[ur.roleType] || 999)
                    .filter(level => level !== 999);

                return levels.length > 0 ? Math.min(...levels) : 999;
            },

            // 특정 권한이 있는지 확인
            hasRole: (roleType) => {
                const { user } = get();
                if (!user?.userRoles) return false;
                return user.userRoles.some(ur => ur.roleType === roleType);
            },

            // 특정 권한 이상인지 확인 (예: MANAGER 이상이면 ADMIN, MANAGER 모두 true)
            hasRoleOrHigher: (minRoleType) => {
                const { getHighestRoleLevel } = get();
                const userLevel = getHighestRoleLevel();
                const requiredLevel = ROLE_LEVELS[minRoleType] || 999;
                return userLevel <= requiredLevel;
            },

            // ADMIN 권한인지 확인
            isAdmin: () => {
                const { hasRole } = get();
                return hasRole('ROLE_ADMIN');
            },

            // MANAGER 이상 권한인지 확인
            isManagerOrHigher: () => {
                const { hasRoleOrHigher } = get();
                return hasRoleOrHigher('ROLE_MANAGER');
            },

            // USER 이상 권한인지 확인 (로그인한 모든 사용자)
            isUserOrHigher: () => {
                const { hasRoleOrHigher } = get();
                return hasRoleOrHigher('ROLE_USER');
            },

            // 서버와 인증 상태 동기화 (쿠키 기반)
            syncAuthState: async () => {
                // HttpOnly 쿠키의 토큰 유효성은 서버 API 호출로 확인
                try {
                    const response = await fetch(`${import.meta.env.VITE_ADMIN_BACKEND_API_URL || 'http://localhost:8080/api/v1'}/auth/me`, {
                        method: 'GET',
                        credentials: 'include', // 쿠키 포함
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    });

                    if (response.ok) {
                        const data = await response.json();
                        if (data.success && data.data) {
                            set({
                                user: data.data,
                                isAuthenticated: data.data.isActive === true,
                            });
                            return true;
                        }
                    }
                } catch (error) {
                    console.error('인증 상태 동기화 오류:', error);
                }

                // 인증 실패 시 상태 초기화
                set({
                    user: null,
                    isAuthenticated: false,
                });
                return false;
            }
        }),
        {
            name: 'auth-storage',
        }
    )
)